import {pick} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import * as benchlingUtil from 'BenchlingUtil';
import {NucleotideType} from 'biojs/BioConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {FolderSummary, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isRegistrySupportedType} from 'constants/RegistryConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import {SourceSummary} from 'constants/SourceConstants';
import {ProteinSummary} from 'molbio/proteins/util/ProteinConstants';
import {ListableItem} from 'search/SearchConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

import {FolderSummaryGraphQL} from '../../graphql-fragments/nav/folders/__generated/FolderSummaryGraphQL';
import {SourceSummaryGraphQL} from '../../graphql-fragments/nav/sources/__generated/SourceSummaryGraphQL';

function isFolder(fileOrFolder: {id: string}): fileOrFolder is StringIDFolder {
  return APIUtil.apiIdToType(fileOrFolder.id) === ModelTypeConstants.FOLDER;
}

export function sourceFromFile(
  file: ListableItem | FileBrowserConstants.FolderItem | {id: string}
): SourceSummary | SourceSummaryGraphQL | null | undefined {
  if (!file.id) {
    return null;
  }
  if (isFolder(file)) {
    return file.source;
  } else {
    // eslint-disable-next-line benchlint/no-in-operator
    return 'folder' in file && file.folder
      ? (file.folder as FolderSummary).source || (file.folder as FolderSummaryGraphQL).source
      : null;
  }
}

// TODO(BNCH-137411): Consider not accepting generic {id: string} for IsInRegistryItem; we can't tell whether
// it's in the registry without a folder or source.
export type IsInRegistryItem = ListableItem | FileBrowserConstants.FolderItem | {id: string};

export function isInRegistry(file: IsInRegistryItem): boolean {
  if (sourceFromFile(file)) {
    return sourceFromFile(file)!.sourceType === ModelTypeConstants.REGISTRY;
  }
  return false;
}

export function isSequenceSummary(
  file: ListableItem | FileBrowserConstants.FolderItem
): file is SequenceSummary {
  return APIUtil.apiIdToType(file.id) === ModelTypeConstants.SEQUENCE;
}

export function isProteinSummary(file: FileBrowserConstants.FolderItem): file is ProteinSummary {
  return APIUtil.apiIdToType(file.id) === ModelTypeConstants.PROTEIN;
}

export function isOligo(file: ListableItem | FileBrowserConstants.FolderItem): boolean {
  return isSequenceSummary(file) && file.type === FileBrowserConstants.SEQUENCE_TYPE.OLIGO;
}

export function isRnaSequence(file: ListableItem | FileBrowserConstants.FolderItem): boolean {
  return (
    isSequenceSummary(file) &&
    file.type === FileBrowserConstants.SEQUENCE_TYPE.SINGLE_STRANDED &&
    file.nucleotideType === NucleotideType.RNA
  );
}

export function isDnaSequence(file: ListableItem | FileBrowserConstants.FolderItem): boolean {
  return (
    isSequenceSummary(file) &&
    file.type === FileBrowserConstants.SEQUENCE_TYPE.DOUBLE_STRANDED &&
    file.nucleotideType === NucleotideType.DNA
  );
}

export function isInventoryFile(item: ListableItem): boolean {
  return isRegistrySupportedType(APIUtil.apiIdToType(item.id));
}

export function getResidues(file: FileBrowserConstants.FolderItem): string | null | undefined {
  if (isProteinSummary(file)) {
    return file.version ? file.version.aminoAcids : null;
  } else if (isSequenceSummary(file)) {
    return file.bases;
  }
  assert(
    !TagSchemaUtil.entityTypeSupportsResidues(APIUtil.apiIdToType(file.id)),
    'Unhandled file type that supports residues'
  );
  return null;
}

export function getResidueWord(entityType: string): Optional<string> {
  switch (entityType) {
    case ModelTypeConstants.PROTEIN:
      return 'amino acid';
    case ModelTypeConstants.SEQUENCE:
    case ModelTypeConstants.DNA_OLIGO:
    case ModelTypeConstants.RNA_OLIGO:
    case ModelTypeConstants.RNA_SEQUENCE:
      return 'base';
    default:
      assert(
        !TagSchemaUtil.entityTypeSupportsResidues(entityType),
        'Unhandled file type that supports residues'
      );
      return null;
  }
}

/*
 * Returns whether a user can edit any properties, or take any actions on entity.
 *
 * Should be updated when more actions are granularized. Currently considers write, edit names/aliases, and
 * editing registry ids.
 */
export function canUserEditAnyPropertiesForEntity(permissions: Permissions): boolean {
  return Object.values(pick(permissions, 'writable', 'canEditNamesAndAliases', 'registryIdsChangeable')).some(
    (perm) => perm
  );
}

export function getInsufficientPermissionsToEditMessage(
  permissions: Permissions,
  entityTypeName: string
): string | null {
  if (permissions.writable) {
    return null;
  }
  const hasPartialEditPerms = canUserEditAnyPropertiesForEntity(permissions);
  if (hasPartialEditPerms) {
    return `Some actions are disabled because you have insufficient permissions for this ${entityTypeName}.`;
  }
  return `Editing disabled on read-only ${entityTypeName}.`;
}

function _baseURL(editURL: string): string {
  return editURL.replace(/\/edit$/, '');
}

/**
 * Given a file's `editURL` and a target `extension`, return the export URL.
 * @param {string} editURL - should match either profiles.editor or start.view_shared_file.
 *                           Future-proofed to also handle the above routes without "/edit" suffix.
 * @param {string} extension - extension of target export format (e.g. "gb", "fasta", "rdf")
 * @param {Object?} options - Appends any keys to the querystring if the correspnoding value is truthy,
 *                            e.g. {dl: true, nocol: 1, nope: 0} generates ?dl&nocol
 * @return {String} - a URL corresponding to either profiles.export_file or start.export_shared_file
 *                    (matching the input `editURL`)
 */
export function getExportURL(editURL: string, extension: string, options: Object = {}): string {
  let qs = Object.entries(options)
    .filter(([k, v]) => v)
    .map(([k, v]) => k)
    .join('&');
  if (qs) {
    qs = `?${qs}`;
  }
  return `${_baseURL(editURL)}.${extension}${qs}`;
}

export function makeCompatibleApiId(urlId: string): string {
  const [filePrefix, fileIdentifier] = urlId.split(/[-_]/, 2);
  if (!fileIdentifier) {
    return urlId;
  }
  return `${filePrefix}_${fileIdentifier}`;
}

function parseItemIdFromURL(
  idIndex: number,
  areValidSegmentsForType: (segments: Array<string>) => boolean,
  url: string | null = null
): string | null {
  const urlToParse = url || window.location.href;
  const {path} = benchlingUtil.parseUri(urlToParse);
  const segments = (path as string).split('/');
  if (!areValidSegmentsForType(segments)) {
    return null;
  }
  return makeCompatibleApiId(segments[idIndex]);
}

// Ex. https://benchling.com/enterprisetx/f/lib_uyOTITVF-enterprise-registry/bfi_rWqhvkMD-m-015014/edit
export function parseFolderItemIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean => segments.length === 6 && segments[2] === 'f';
  return parseItemIdFromURL(4, areSegmentsValid, url);
}

// Ex. https://benchling.com/samples/containers/con_u6fVZox2
export function parseSampleIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    segments.length === 4 && segments[1] === 'samples';
  return parseItemIdFromURL(3, areSegmentsValid, url);
}

// Ex. https://benchling.com/analytics/dashboards/axdash_SkfAQsjR-read-only-dashboard
export function parseInsightsIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    segments.length === 4 && segments[1] === 'analytics' && segments[2] === 'dashboards';
  return parseItemIdFromURL(3, areSegmentsValid, url);
}

export function parseAnalysisIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    segments.length === 4 && segments[1] === 'analytics' && segments[2] === 'analyses';
  return parseItemIdFromURL(3, areSegmentsValid, url);
}

export function parseWorkflowsIdFromURL(url: string | null = null): string | null {
  return parseWorkflowIdFromURL(url) || parseStageEntryIdFromURL(url) || parseWorkflowTemplateIdFromURL(url);
}

// Ex. https://benchling.com/workflows/view/wfw_B2hfo1qs
function parseWorkflowIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    segments.length === 4 &&
    (segments[1] === 'workflows' || segments[1] === 'legacy-workflows') &&
    segments[2] === 'view';
  return parseItemIdFromURL(3, areSegmentsValid, url);
}

// Ex. https://benchling.com/workflows/stage-entries/stgentr_qQxd3jkN
// Ex. https://benchling.com/workflows/stage-entries/NsfsrheI
// Ex. https://benchling.com/workflows/stage-entries/stgentr_fQ4Z47jm/view
function parseStageEntryIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    (segments.length === 4 || segments.length === 5) &&
    (segments[1] === 'workflows' || segments[1] === 'legacy-workflows') &&
    segments[2] === 'stage-entries';
  const id = parseItemIdFromURL(3, areSegmentsValid, url);
  return id ? APIUtil.withPrefixIfMissing(id, ModelTypeConstants.STAGE_ENTRY) : null;
}

// Ex. https://benchling.com/workflows/templates/view/wfwtmpl_abyHlq9Q
function parseWorkflowTemplateIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    segments.length === 5 &&
    (segments[1] === 'workflows' || segments[1] === 'legacy-workflows') &&
    segments[2] === 'templates' &&
    segments[3] === 'view';
  return parseItemIdFromURL(4, areSegmentsValid, url);
}

// Ex. https://benchling.com/requests/req_X0dhw90n
export function parseRequestsIdFromURL(url: string | null = null): string | null {
  const areSegmentsValid = (segments: Array<string>): boolean =>
    segments.length === 3 && segments[1] === 'requests';
  return parseItemIdFromURL(2, areSegmentsValid, url);
}
